/**
 * Side bar implementation.
 *
 * Copyright © Ircam Amplify 2025-2026
 */
import { EXTERNAL_URLS } from '@/constant/externalUrl'
import { useState } from 'react'
import { AiOutlineBarChart, AiOutlineHome, AiOutlineTeam } from 'react-icons/ai'
import { BiBot, BiPlus } from 'react-icons/bi'
import { FaChevronDown, FaChevronUp, FaRegQuestionCircle } from 'react-icons/fa'
import { FiChevronLeft, FiChevronRight, FiLayers } from 'react-icons/fi'
import { PiPuzzlePiece } from 'react-icons/pi'
import { NavLink } from 'react-router-dom'

import { AppRouteUrl, getRouteTitle } from '@/AppRoute'
import {
  getLayoutState,
  getUserState,
  setModuleSelectorCurrentPageId,
  setSideBarCollapsed,
  useDispatch,
  useSelector,
} from '@/store'
import { isDebugMockup } from '@/utils/TestUtil'
import { AccountUtil } from '@amplify3/duplo-back-end'
import Logo from './Logo'
import PopOver from './PopOver'
import './Sidebar.css'

interface Props {
  Icon: any
  link: string
  label: string
}

function SideBarItem({ Icon, link, label }: Props) {
  const { sideBarCollapsed } = useSelector(getLayoutState)
  return (
    <div className="sidebar-item">
      <NavLink
        to={link}
        className={`sidebar-item-link${sideBarCollapsed ? ' collapsed' : ''}`}
      >
        {!sideBarCollapsed && (
          <>
            <Icon size={24} />
            <span className="sidebar-item-label">{label}</span>
          </>
        )}
        {sideBarCollapsed && (
          <PopOver link={link}>
            <Icon size={24} />
            <div className="popover-right sidebar-popover-fit">
              <div className="left-arrow left-arrow-small" />
              <div className="sidebar-popover-menu light-text">{label}</div>
            </div>
          </PopOver>
        )}
      </NavLink>
    </div>
  )
}

const SideBarCTA = ({ Icon, link, label }: Props) => {
  const dispatch = useDispatch()
  const { sideBarCollapsed } = useSelector(getLayoutState)
  return (
    <div className="sidebar-cta">
      <div onClick={() => dispatch(setModuleSelectorCurrentPageId(0))}>
        <NavLink
          to={link}
          className={`sidebar-cta-link${sideBarCollapsed ? ' collapsed' : ''}`}
        >
          {!sideBarCollapsed && (
            <>
              <Icon size={24} />
              <span className="sidebar-item-label">{label}</span>
            </>
          )}
          {sideBarCollapsed && (
            <PopOver link={link}>
              <Icon size={24} />
              <div className="popover-right sidebar-popover-fit">
                <div className="left-arrow left-arrow-small" />
                <div className="sidebar-popover-menu light-text">{label}</div>
              </div>
            </PopOver>
          )}
        </NavLink>
      </div>
    </div>
  )
}

const SideBarHeader = () => {
  const dispatch = useDispatch()
  const { sideBarCollapsed } = useSelector(getLayoutState)

  // Toggle sidebar collapse state when clicking the header
  const onClick = () => {
    dispatch(setSideBarCollapsed(!sideBarCollapsed))
  }

  const ToggleIcon = sideBarCollapsed ? FiChevronRight : FiChevronLeft
  return (
    <div
      id="sidebar-header"
      className="sidebar-header"
      onClick={onClick}
      onKeyUp={onClick}
      role="button"
      tabIndex={0}
    >
      <div
        id="sidebar-logo"
        className={`sidebar-logo${sideBarCollapsed ? ' collapsed' : ''}`}
      >
        <Logo
          color={isDebugMockup() ? 'red' : 'white'}
          alternative={sideBarCollapsed}
          className="sidebar-logo-icon"
          width={sideBarCollapsed ? 30 : 150}
          height={sideBarCollapsed ? 30 : 50}
        />
      </div>
      <div id="sidebar-toggle" className="sidebar-toggle">
        <ToggleIcon className="sidebar-toggle-icon" />
      </div>
    </div>
  )
}

function SideBarSeparator() {
  return <div id="separator" className="sidebar-separator" />
}

// Dropdown menu for resources when sidebar is collapsed
function CollapsedAddMenu({ children = [] }: { children: any }) {
  return (
    <div className="sidebar-collapsed-menu">
      <PopOver>
        <FiLayers size={24} className="sidebar-resources-icon" />
        <div className="popover-right sidebar-popover-fit">
          <div className="left-arrow left-arrow-small" />
          <div className="sidebar-popover-menu">
            <div className="sidebar-resources-title">Resources</div>
            {children}
          </div>
        </div>
      </PopOver>
    </div>
  )
}

// Dropdown menu for resources when sidebar is expanded
function SideBarAddMenu({ children = [] }: { children: any }) {
  const [isOpen, setIsOpen] = useState(false)
  const OpenIcon = isOpen ? FaChevronUp : FaChevronDown
  return (
    <div id="sidebar-resources-menu" className="sidebar-resources-menu">
      <div
        id="sidebar-resources-menu-header"
        className="sidebar-resources-header"
        onClick={() => setIsOpen(!isOpen)}
        onKeyUp={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
      >
        <div className="sidebar-resources-row">
          <div className="sidebar-resources-label-group">
            <FiLayers size={24} className="sidebar-resources-icon" />
            <span className="sidebar-resources-text">Resources</span>
          </div>
          <OpenIcon className="sidebar-resources-chevron" />
        </div>
      </div>
      <div
        id="sidebar-resources-menu-contents"
        className={`collapsible-height sidebar-resources-contents${
          isOpen ? ' open' : ''
        }`}
      >
        {children}
      </div>
    </div>
  )
}

function Support() {
  const { sideBarCollapsed } = useSelector(getLayoutState)
  const url = EXTERNAL_URLS.CONTACT

  return (
    <div className="sidebar-support">
      {sideBarCollapsed ? (
        <a href={url} target="_blank" rel="noreferrer">
          <FaRegQuestionCircle
            className="sidebar-support-icon-collapsed"
            size={22}
          />
        </a>
      ) : (
        <div className="sidebar-support-row">
          <FaRegQuestionCircle className="sidebar-support-icon" size={22} />
          <a
            href={url}
            className="sidebar-support-link"
            target="_blank"
            rel="noreferrer"
          >
            Support
          </a>
        </div>
      )}
    </div>
  )
}

const SideBar = () => {
  const { companyConsumption, userInfo } = useSelector(getUserState)

  const { sideBarCollapsed } = useSelector(getLayoutState)
  const contents = (
    <ul>
      <li className="sidebar-resource-item">
        <a
          href={import.meta.env.VITE_API_DOCS_BASE_URI}
          className="sidebar-resource-link"
          target="_blank"
          rel="noreferrer"
        >
          API Explorer
        </a>
      </li>

      <li className="sidebar-resource-item">
        <NavLink
          to={AppRouteUrl.GeneralTermsConditions}
          className="sidebar-resource-link"
        >
          {getRouteTitle(AppRouteUrl.GeneralTermsConditions)}
        </NavLink>
      </li>

      <li className="sidebar-resource-item">
        <NavLink
          to={AppRouteUrl.PrivatePolicy}
          className="sidebar-resource-link"
        >
          {getRouteTitle(AppRouteUrl.PrivatePolicy)}
        </NavLink>
      </li>
    </ul>
  )

  return (
    <aside
      id="default-sidebar"
      className={`sidebar${sideBarCollapsed ? ' collapsed' : ''}`}
      aria-label="Sidebar"
    >
      <div className="sidebar-content">
        <SideBarHeader />

        {/* Group 1: CTA */}
        <ul className="sidebar-nav-group">
          <li key="cta">
            <SideBarCTA
              link={AppRouteUrl.TaskCreation}
              label="Create a new task"
              Icon={BiPlus}
            />
          </li>
        </ul>

        {/* Group 2: Home and Tasks */}
        <ul className="sidebar-nav-group">
          <li key="home">
            <SideBarItem
              Icon={AiOutlineHome}
              link={AppRouteUrl.Home}
              label={getRouteTitle(AppRouteUrl.Home) ?? ''}
            />
          </li>
          <li key="tasks">
            <SideBarItem
              Icon={BiBot}
              link={AppRouteUrl.TaskHistory}
              label={getRouteTitle(AppRouteUrl.TaskHistory) ?? ''}
            />
          </li>
        </ul>

        <SideBarSeparator />

        {/* Group 3: API Credentials and Webhooks */}
        {AccountUtil.isAdminUser(userInfo) && (
          <>
            <ul className="sidebar-nav-group">
              <li key="api-keys-webhook">
                <SideBarItem
                  Icon={PiPuzzlePiece}
                  link={AppRouteUrl.ApiKey}
                  label={getRouteTitle(AppRouteUrl.ApiKey) ?? ''}
                />
              </li>
            </ul>

            <SideBarSeparator />
          </>
        )}

        {/* Group 4: Usage and teams */}
        <ul className="sidebar-nav-group">
          <li key="usage">
            <SideBarItem
              Icon={AiOutlineBarChart}
              link={AppRouteUrl.Usage}
              label={getRouteTitle(AppRouteUrl.Usage) ?? ''}
            />
          </li>
          {AccountUtil.isCompanyTierEnterprise(companyConsumption) && (
            <li key="team">
              <SideBarItem
                Icon={AiOutlineTeam}
                link={AppRouteUrl.Team}
                label={getRouteTitle(AppRouteUrl.Team) ?? ''}
              />
            </li>
          )}
        </ul>

        <SideBarSeparator />

        {/* Group 5: Resources and Support */}
        <ul className="sidebar-nav-group">
          <li key="resources">
            {sideBarCollapsed ? (
              <CollapsedAddMenu>{contents}</CollapsedAddMenu>
            ) : (
              <SideBarAddMenu>{contents}</SideBarAddMenu>
            )}
          </li>
          <li key="support">
            <Support />
          </li>
        </ul>
      </div>
    </aside>
  )
}

export default SideBar
